/**
 * Inteligência de Custo › Rede e preços: quanto custa cada procedimento em cada
 * rede (ex.: rede direta x intercâmbio), e quanto o mesmo volume custaria pelo
 * outro lado.
 *
 * Regras de leitura que a tela não esconde:
 *  - só entra o procedimento que os dois lados fazem, e o total compara o MESMO
 *    volume aos preços de cada lado (comparar totais mediria volume, não preço);
 *  - o que está sem classificação aparece antes do número;
 *  - cada número tem um "i" com origem, leitura e limite.
 * Backend: /resultado-plano/utilizacao/rede/* (servico_rede.py).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, RefreshCw, Save, Plus, Trash2 } from "lucide-react";
import { LineChart, Line, BarChart, Bar, Cell, LabelList, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, ResponsiveContainer, ReferenceLine } from "recharts";
import toast from "react-hot-toast";
import api from "../../services/api";
import InfoTip from "../../components/InfoTip";
import SimuladorReajuste from "./SimuladorReajuste";
import CampoBusca from "./CampoBusca";

const T = {
  ink: "#2c3e50", inkSoft: "#7f8c8d", line: "#e8e8e8", teal: "var(--brand, #20a490)", tealSoft: "#e8f6f4",
  risco: "#e74c3c", riscoSoft: "#fdecea", ambar: "#f39c12", ambarSoft: "#fef5e7", azul: "#3498db", verde: "#27ae60",
};
const MONO = "'SF Mono','JetBrains Mono',Consolas,monospace";
const BASE = "/resultado-plano/utilizacao/rede";

const vazio = (v) => v === null || v === undefined || Number.isNaN(v);
const fmtR = (v) => (vazio(v) ? "—" : `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtN = (v, casas = 0) => (vazio(v) ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas }));
const fmtP = (v) => (vazio(v) ? "—" : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
const fmtK = (v) => (Math.abs(v) >= 1e6 ? `${fmtN(v / 1e6, 1)} mi` : Math.abs(v) >= 1e3 ? `${fmtN(v / 1e3, 0)} mil` : fmtN(v, 0));
const dataBR = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const mesBR = (m) => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : "");
const mesMenos = (m, n) => {
  const t = Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7)) - 1 - n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
};
const curto = (s, n = 38) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s);
const dataHoraBR = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "—");
const erroDe = (e, padrao) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : padrao; };

const TH = { padding: "7px 8px", fontWeight: 600, fontSize: 12, color: T.inkSoft, borderBottom: `1px solid ${T.line}`, textAlign: "left", position: "sticky", top: 0, background: "#fff" };
const TD = { padding: "6px 8px", fontSize: 13, color: T.ink, borderBottom: `1px solid ${T.line}` };
const NUM = { ...TD, fontFamily: MONO, textAlign: "right", whiteSpace: "nowrap" };
const card = { background: "#fff", border: `1px solid ${T.line}`, borderRadius: 12, padding: 18, marginTop: 14 };
const btn = (ativo) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${ativo ? T.teal : T.line}`, background: ativo ? T.tealSoft : "#fff", color: ativo ? T.teal : T.ink });
const campo = { fontSize: 12, padding: "5px 8px", border: `1px solid ${T.line}`, borderRadius: 6, background: "#fff" };

const TIP = {
  tela: "Compara o preço de cada procedimento entre dois lados: duas categorias de rede (por exemplo, rede direta e intercâmbio) ou prestadores específicos. O valor é o que a operadora pagou ao prestador, com a glosa já descontada pelo sistema de origem. Só entram os procedimentos feitos pelos dois lados no período. Limite: valor pago não é custo de produção, e o mês mais recente pode estar incompleto porque a conta chega depois do atendimento.",
  resumo: "Quanto foi pago no período a cada categoria de rede, com a classificação de hoje. A classificação vem do cadastro de prestadores do ERP pela regra da operadora, e o gestor pode marcar exceções. Reembolso é a conta sem prestador.",
  diferenca: "Pega o volume que o primeiro lado fez em cada procedimento e calcula quanto teria custado aos preços do segundo lado. Assim a diferença mede preço, não volume. Positivo: o primeiro lado saiu mais barato. Só entram procedimentos com o volume mínimo nos dois lados.",
  cobertura: "Quanto do valor de cada lado entrou na comparação. O resto são procedimentos que só um lado fez ou que ficaram abaixo do volume mínimo. Cobertura baixa quer dizer que a conclusão vale para uma parte pequena do gasto.",
  semClass: "Valor pago a prestadores sem categoria (fora do cadastro do ERP ou sem regra que se aplique). Ele fica fora dos dois lados. Acima de 10% do valor do período, a comparação pode mudar quando esses prestadores forem classificados.",
  preco: "Preço médio ponderado pelo volume: soma do valor pago dividida pela soma das quantidades pagas, juntando todos os prestadores do lado no período (não é a média simples dos preços dos prestadores). Item glosado por inteiro não entra. Clique na linha para ver prestador por prestador, com o menor e o maior preço.",
  minimo: "Procedimento com poucas unidades em um dos lados: a média de poucos casos não descreve preço. Fica listado à parte e não entra na diferença total.",
  evolucao: "A mesma diferença calculada mês a mês, nos seis meses que terminam na competência escolhida (ou no intervalo escolhido). Um mês atípico engana; a linha mostra se a vantagem se repete.",
  ondeDiferenca: "Os procedimentos que mais pesam na diferença: volume do primeiro lado vezes a diferença de preço por unidade. Verde: o primeiro lado é mais barato nesse procedimento; vermelho: mais caro. A soma de todos os procedimentos dá a diferença total acima.",
  sintAnal: "Sintético: o preço médio de cada prestador no período (soma do valor ÷ soma das quantidades). Analítico: cada atendimento, com o valor que foi pago de verdade naquela conta. A média esconde a troca de tabela; no analítico o reajuste aparece.",
  serieUnit: "Valor por unidade de cada lado em cada mês (média ponderada do mês). Linha em degrau: quando a tabela do prestador muda, a linha sobe ou desce naquele mês. Passe o mouse para ver o menor e o maior valor do mês. Conta glosada por inteiro não entra.",
  base: "Data do atendimento: o mês em que o atendimento foi feito. É a certa para comparar preço: a tabela do prestador muda (reajuste) e uma conta apresentada hoje pode ser de um atendimento antigo, com preço antigo. Competência: o mês em que a conta foi apresentada (como era antes). Por data do atendimento, os meses recentes ficam incompletos até as contas chegarem.",
  totalLados: "Quanto foi pago a cada lado no período, somando todos os procedimentos (não só os que os dois lados fazem). Com hospital ou prestador escolhido, o gráfico mostra só os dois lados; com categorias de rede, mostra a carteira por categoria.",
  hospital: "Compara o MESMO hospital pelos dois cartões. Cartão ELOSAÚDE: a conta que o próprio hospital cobra. Cartão Unimed: a conta que a Unimed cobra com o hospital como executante (CNPJ do executante na conta do ERP). A lista mostra só hospitais com atendimento pelos dois cartões no período.",
  indireta: "Junta todas as categorias comparáveis que não são a rede direta (por exemplo, intercâmbio e outras reciprocidades) num lado só.",
  classif: "Prestadores do período, do maior valor para o menor, com a categoria de cada um e os campos do cadastro do ERP que levaram a ela. A medida de progresso é a parte do VALOR classificada, não a quantidade de prestadores.",
  regra: "A regra transforma o cadastro do ERP em categoria. Cada linha diz: se o prestador tem este tipo, grupo, contratualização ou credenciamento, ele vai para esta categoria. A primeira linha que se aplica decide; o que nenhuma linha pega vai para Outros, para revisar o cadastro.",
};

// ---------- seletor de lado ----------
// "Toda a rede indireta": as categorias comparáveis que não são a direta, num lado só.
export const opcaoIndireta = (categorias) => {
  const outras = categorias.filter((c) => c.comparavel && c.codigo !== "DIRETA");
  if (outras.length < 2) return null;
  return { valor: `rede:${outras.map((c) => c.codigo).join(",")}`, rotulo: `Toda a rede indireta (${outras.map((c) => c.rotulo).join(" + ")})` };
};

// CPF (11) ou CNPJ (14) só com dígitos -> formatado
export const fmtDoc = (d) => {
  const x = String(d || "").replace(/\D/g, "");
  if (x.length === 14) return x.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  if (x.length === 11) return x.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  return x;
};

// Busca de prestador igual à do beneficiário: digita parte do nome, o código ou o
// CPF/CNPJ e escolhe na lista (cada sugestão traz o CNPJ, para separar cadastros
// parecidos como "HOSPITAL BAIA SUL SA" e "HOSPITAL BAIA SUL S A").
export function BuscaPrestador({ onEscolher, placeholder = "Buscar prestador (nome, código ou CNPJ)" }) {
  const [q, setQ] = useState("");
  const [lista, setLista] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  useEffect(() => {
    const t = q.trim();
    if (t.length < 3) { setLista([]); return undefined; }
    setBuscando(true);
    const h = setTimeout(() => {
      api.get(`${BASE}/prestadores/busca`, { params: { q: t } })
        .then((r) => { setLista(r.data || []); setAberto(true); }).catch(() => setLista([])).finally(() => setBuscando(false));
    }, 300);
    return () => clearTimeout(h);
  }, [q]);
  return (
    <div style={{ position: "relative" }}>
      <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => lista.length && setAberto(true)} onBlur={() => setTimeout(() => setAberto(false), 200)}
             placeholder={placeholder} aria-label={placeholder} style={{ ...campo, minWidth: 230, width: "100%", boxSizing: "border-box", fontSize: 12 }} />
      {aberto && (lista.length > 0 || buscando) && (
        <div style={{ position: "absolute", zIndex: 20, top: "100%", left: 0, right: 0, minWidth: 340, background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, boxShadow: "0 6px 18px rgba(0,0,0,.12)", maxHeight: 280, overflowY: "auto" }}>
          {buscando && <div style={{ padding: 8, fontSize: 12, color: T.inkSoft }}>Buscando…</div>}
          {lista.map((p) => (
            <div key={p.cd_prestador} onMouseDown={() => { onEscolher(p); setQ(""); setLista([]); setAberto(false); }}
                 style={{ padding: "7px 10px", cursor: "pointer", borderBottom: `1px solid ${T.line}`, fontSize: 12.5 }}>
              <div style={{ fontWeight: 600, color: T.ink }}>{p.nm_prestador || `Prestador ${p.cd_prestador}`}</div>
              <div style={{ color: T.inkSoft, fontSize: 11.5 }}>{p.nr_cpf_cgc ? `${p.nr_cpf_cgc.length === 14 ? "CNPJ" : "CPF"} ${fmtDoc(p.nr_cpf_cgc)}` : "sem CPF/CNPJ no cadastro"} · código {p.cd_prestador}{p.rotulo_rede ? ` · ${p.rotulo_rede}` : ""}</div>
            </div>))}
          {!buscando && lista.length === 0 && <div style={{ padding: 8, fontSize: 12, color: T.inkSoft }}>Nenhum prestador encontrado.</div>}
        </div>
      )}
    </div>
  );
}

export function SeletorLado({ rotulo, valor, onChange, categorias, prestadores, extras = {}, comBusca = false }) {
  const presentes = prestadores.some((p) => `prestador:${p.cd_prestador}` === valor);
  const indireta = opcaoIndireta(categorias);
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: T.inkSoft }}>
      {rotulo}
      <select value={valor} onChange={(e) => onChange(e.target.value)} style={{ ...campo, minWidth: 230 }}>
        <optgroup label="Categorias de rede">
          {categorias.filter((c) => c.comparavel).map((c) => <option key={c.codigo} value={`rede:${c.codigo}`}>{c.rotulo}</option>)}
          {indireta && <option value={indireta.valor}>{indireta.rotulo}</option>}
        </optgroup>
        {extras[valor] && <optgroup label={valor.startsWith("hospital:") ? "Mesmo hospital" : "Escolhido na busca"}><option value={valor}>{extras[valor]}</option></optgroup>}
        <optgroup label="Prestadores (maiores valores)">
          {!presentes && valor.startsWith("prestador:") && <option value={valor}>Prestador {valor.slice(10)}</option>}
          {prestadores.map((p) => <option key={p.cd_prestador} value={`prestador:${p.cd_prestador}`}>{p.nm_prestador}</option>)}
        </optgroup>
      </select>
      {comBusca && <BuscaPrestador onEscolher={comBusca} />}
    </label>
  );
}

// ---------- como a tela calcula (passo a passo) ----------
// Explicação completa, para quem lê não precisar perguntar. O exemplo usa um
// procedimento REAL do período, com a conta feita com os números da tela.
const H = { fontSize: 13, fontWeight: 700, color: T.ink, margin: "14px 0 4px" };
const P = { fontSize: 13, color: T.ink, margin: "0 0 6px", lineHeight: 1.55 };
const LI = { fontSize: 13, color: T.ink, lineHeight: 1.55, marginBottom: 3 };

function Metodologia({ comp, ra, rb, minQt, semCopart }) {
  const ex = useMemo(() => {
    // o procedimento que mais pesa no valor do primeiro lado: exemplo que importa
    const itens = (comp?.comparaveis || []).filter((i) => !vazio(i.unit_a) && !vazio(i.unit_b) && i.qt_a > 0 && i.vl_a > 0);
    return itens.sort((x, y) => y.vl_a - x.vl_a)[0] || null;
  }, [comp]);
  const ag = comp?.agregado;
  const valorTxt = semCopart ? "valor pago ao prestador menos a coparticipação do beneficiário" : "valor pago ao prestador";
  return (
    <div style={{ marginTop: 12, padding: "6px 18px 14px", borderRadius: 10, border: `1px solid ${T.line}`, background: "#fcfdfd" }}>
      <div style={H}>1. O que esta tela responde</div>
      <p style={P}>
        Para os mesmos procedimentos, quanto a operadora paga em um lado ({ra}) e quanto paga no outro ({rb}).
        E, com isso, quanto o volume que {ra} atendeu teria custado se tivesse sido pago pelos preços de {rb}.
        É uma comparação de <b>preço</b>, não de volume nem de qualidade do atendimento.
      </p>

      <div style={H}>2. De onde vêm os dados</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        <li style={LI}>Contas médicas do ERP (uma linha por item de conta), copiadas para o AURA todos os dias.</li>
        <li style={LI}>O mês é a <b>competência</b>: a data de apresentação da conta, não a data do atendimento. Um atendimento de agosto pode entrar em setembro se a conta chegou em setembro.</li>
        <li style={LI}>O valor usado é o <b>{valorTxt}</b>. A glosa já vem descontada pelo ERP: o que foi glosado não é somado nem subtraído de novo.</li>
        <li style={LI}>O regime (pré ou pós-pagamento) e o mês escolhidos no topo filtram tudo o que aparece aqui.</li>
      </ul>

      <div style={H}>3. Quem está em cada lado</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        <li style={LI}>Cada prestador recebe uma categoria de rede (rede direta, intercâmbio, outras reciprocidades etc.) pela <b>regra de classificação</b>, que lê o cadastro de prestadores do ERP (tipo, grupo, contratualização e credenciamento). O gestor pode corrigir um prestador à mão; a tela mostra quem foi marcado assim.</li>
        <li style={LI}>Um prestador conta em um lado só. Se o primeiro lado for um prestador que também faz parte da rede do segundo lado, ele sai do segundo: a comparação é dele contra o resto.</li>
        <li style={LI}>Prestador sem classificação, ou em Outros, fica fora dos dois lados. Se isso passar de 10% do valor do período, a tela avisa em amarelo.</li>
        <li style={LI}>Reembolso não tem prestador e nunca entra na comparação.</li>
      </ul>

      <div style={H}>4. Preço por unidade: é preço médio?</div>
      <p style={P}>
        <b>Sim, é o preço médio ponderado pelo volume.</b> Para cada procedimento e cada lado:
        <b> soma do valor pago ÷ soma das quantidades pagas</b>, juntando todos os prestadores daquele lado no período.
      </p>
      <p style={P}>
        Não é a média simples dos preços dos prestadores. Exemplo: um prestador fez 100 unidades a R$ 10,00 e outro fez 10 unidades a R$ 40,00.
        O preço médio ponderado é (1.000 + 400) ÷ 110 = <b>R$ 12,73</b>. A média simples daria R$ 25,00, como se os dois pesassem igual, o que não reflete o que a operadora pagou.
      </p>
      <p style={P}>
        A média esconde a variação entre prestadores. Para ver quem cobra quanto, clique no procedimento na tabela:
        aparece prestador por prestador, com o menor e o maior preço do lado.
      </p>

      <div style={H}>5. Quais procedimentos entram na conta</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        <li style={LI}>Só os procedimentos (mesmo código) feitos <b>pelos dois lados</b> no período. O que só um lado faz não tem com o que comparar.</li>
        <li style={LI}>{minQt > 0 ? <>É preciso ter pelo menos <b>{fmtN(minQt)} unidades em cada lado</b>. Abaixo disso a média de poucos casos não descreve preço; esses procedimentos ficam listados à parte e não entram no total.</> : <>Sem volume mínimo: qualquer procedimento feito pelos dois lados entra, mesmo com uma unidade só (a média de poucos casos pode enganar).</>}</li>
        <li style={LI}>Item glosado por inteiro (quantidade paga zero) não entra no preço, porque não houve pagamento; aparece contado à parte.</li>
        <li style={LI}>Conta sem quantidade informada no ERP conta como 1 unidade, e o item fica marcado com *.</li>
      </ul>

      <div style={H}>6. Como sai a diferença total</div>
      <p style={P}>
        Para cada procedimento comparável: <b>quantidade de {ra} × preço de {rb}</b> = quanto o volume de {ra} custaria aos preços de {rb}.
        Somando todos os procedimentos e comparando com o que {ra} de fato recebeu, sai a diferença.
        Usar o mesmo volume dos dois lados é o que faz a conta medir preço: se compararmos os totais pagos a cada rede, a rede que atende mais pessoas pareceria sempre mais cara.
      </p>
      {ex && (
        <div style={{ background: T.tealSoft, borderRadius: 8, padding: "10px 12px", margin: "6px 0" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.ink, marginBottom: 4 }}>Exemplo real deste período: {ex.descricao || ex.procedimento} ({ex.procedimento})</div>
          <div style={{ fontSize: 13, color: T.ink, lineHeight: 1.6, fontFamily: MONO }}>
            {ra}: {fmtR(ex.vl_a)} ÷ {fmtN(ex.qt_a)} un. = {fmtR(ex.unit_a)} por unidade<br />
            {rb}: {fmtR(ex.vl_b)} ÷ {fmtN(ex.qt_b)} un. = {fmtR(ex.unit_b)} por unidade<br />
            {ra} aos preços de {rb}: {fmtN(ex.qt_a)} × {fmtR(ex.unit_b)} = {fmtR(ex.qt_a * ex.unit_b)}<br />
            Diferença neste procedimento: {fmtR(ex.qt_a * ex.unit_b)} − {fmtR(ex.vl_a)} = <b>{fmtR(ex.qt_a * ex.unit_b - ex.vl_a)}</b>
            {" "}({Math.abs(ex.qt_a * ex.unit_b - ex.vl_a) < 0.005 ? "mesmo preço" : ex.qt_a * ex.unit_b - ex.vl_a > 0 ? `${ra} mais barato` : `${ra} mais caro`})
          </div>
        </div>
      )}
      {ag && (
        <p style={P}>
          Somando os {fmtN(comp.cobertura.qt_proc_comparaveis)} procedimentos comparáveis do período: {ra} recebeu {fmtR(ag.a_preco_a)};
          aos preços de {rb} seriam {fmtR(ag.a_preco_b)}; diferença de <b>{fmtR(ag.economia_de_a)}</b>
          {ag.economia_de_a >= 0 ? ` (o quanto ${ra} custou a menos).` : ` (o quanto ${ra} custou a mais).`}
          {" "}A conta inversa também existe: o volume de {rb} aos preços de {ra} custaria {fmtR(ag.b_preco_a)}, contra {fmtR(ag.b_preco_b)} pagos.
        </p>
      )}

      <div style={H}>7. Cobertura: quanto do gasto a conclusão representa</div>
      <p style={P}>
        É a parte do valor de cada lado que entrou na comparação. O restante são procedimentos que só um lado faz ou que ficaram abaixo do volume mínimo.
        Se a cobertura for baixa, a diferença vale só para aquela fatia do gasto e não deve ser generalizada para a rede inteira.
      </p>

      <div style={H}>8. Os gráficos</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        <li style={LI}><b>Valor pago por categoria de rede:</b> tudo o que foi pago no período, dividido pela categoria de cada prestador (inclusive o que não entra na comparação, como farmácia e reembolso).</li>
        <li style={LI}><b>Diferença mês a mês:</b> a mesma conta do item 6 feita mês a mês, nos seis meses que terminam na competência escolhida (ou no intervalo escolhido). Serve para ver se a vantagem se repete ou se foi um mês fora da curva.</li>
        <li style={LI}><b>Onde está a diferença:</b> os procedimentos que mais pesam no total (quantidade de {ra} × diferença de preço por unidade). Verde: {ra} é mais barato naquele procedimento; vermelho: mais caro. Clicar na barra abre o procedimento na tabela.</li>
      </ul>

      <div style={H}>9. O que esta conta não diz (limites)</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        <li style={LI}><b>Pacotes e diárias:</b> alguns prestadores cobram um pacote em que a diária inclui materiais, exames ou honorários; outros cobram tudo separado. O mesmo código pode, então, significar coisas diferentes nos dois lados. Antes de concluir sobre diárias e internações, abra o procedimento e veja os prestadores.</li>
        <li style={LI}><b>Mesmo código, casos diferentes:</b> um lado pode atender casos mais graves com o mesmo código. A conta compara preço pago, não a complexidade de quem foi atendido.</li>
        <li style={LI}><b>Mês recente incompleto:</b> as contas chegam depois do atendimento; o último mês pode mudar quando o restante for apresentado.</li>
        <li style={LI}><b>Só a conta médica:</b> entra o que foi pago na conta. Custos fora dela (por exemplo, taxas cobradas à parte ou custo administrativo da operadora) não aparecem aqui.</li>
        <li style={LI}><b>Preço não é tudo:</b> a tela não mede acesso, prazo, qualidade ou se o beneficiário tem a rede direta perto de casa. É um insumo para a decisão, não a decisão.</li>
      </ul>

      <div style={H}>10. Como conferir</div>
      <p style={P}>
        No fim da tela, "De onde vêm estes números" repete o período e os filtros usados, e o "detalhe técnico" mostra a tabela e as colunas exatas
        (fonte, campo de data, campo de valor, regra de quantidade). Com isso qualquer pessoa reproduz a conta direto no banco.
      </p>
    </div>
  );
}

// ---------- abrir um procedimento ----------
function DetalheItem(props) {
  const [aba, setAba] = useState("sintetico");
  const bt = (k, r, tip) => (
    <button onClick={() => setAba(k)} style={{ border: 0, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
      background: aba === k ? T.ink : "#fff", color: aba === k ? "#fff" : T.ink }} title={tip}>{r}</button>);
  return (
    <div style={{ background: "#fafbfc", padding: 10, borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ display: "inline-flex", border: `1px solid ${T.line}`, borderRadius: 8, overflow: "hidden" }}>
          {bt("sintetico", "Sintético: por prestador")}{bt("analitico", "Analítico: cada atendimento")}
        </div>
        <InfoTip w={360} texto={TIP.sintAnal} />
      </div>
      {aba === "sintetico" ? <DetalheSintetico {...props} /> : <DetalheAnalitico {...props} />}
    </div>
  );
}

// Cada atendimento do procedimento nos dois lados, com o valor real, e o valor
// unitário mês a mês: o degrau do reajuste aparece no gráfico (#242 item 4).
function DetalheAnalitico({ params, procedimento, rotuloA, rotuloB }) {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [lado, setLado] = useState("");
  const [baixando, setBaixando] = useState(false);
  useEffect(() => { setPagina(1); }, [params, procedimento, lado]);
  useEffect(() => {
    let vivo = true;
    setErro(null);
    api.get(`${BASE}/analitico`, { params: { ...params, procedimento, pagina, lado: lado || undefined } })
      .then((r) => vivo && setD(r.data)).catch((e) => vivo && setErro(erroDe(e, "Não foi possível abrir os atendimentos.")));
    return () => { vivo = false; };
  }, [params, procedimento, pagina, lado]);
  const baixar = async () => {
    setBaixando(true);
    try {
      const r = await api.get(`${BASE}/analitico.csv`, { params: { ...params, procedimento }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `atendimentos_${procedimento}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Não foi possível exportar."); }
    setBaixando(false);
  };
  if (erro) return <div style={{ color: T.risco, fontSize: 12, padding: 8 }}>{erro}</div>;
  if (!d) return <div style={{ color: T.inkSoft, fontSize: 12, padding: 8 }}>Carregando atendimentos…</div>;
  const total = lado ? (d.total_linhas[lado] || 0) : (d.total_linhas.A || 0) + (d.total_linhas.B || 0);
  const paginas = Math.max(1, Math.ceil(total / d.por_pagina));
  const serie = d.serie.map((m) => ({ mes: mesBR(m.mes), A: m.A?.unit ?? null, B: m.B?.unit ?? null, Amin: m.A?.unit_min, Amax: m.A?.unit_max, Bmin: m.B?.unit_min, Bmax: m.B?.unit_max }));
  const rot = { A: rotuloA, B: rotuloB };
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
        Valor por unidade mês a mês ({d.base === "atendimento" ? "pela data do atendimento" : "pela competência"}) <InfoTip w={340} texto={TIP.serieUnit} />
      </div>
      {serie.length > 0 ? (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={serie} margin={{ top: 6, right: 16, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$ ${fmtN(v)}`} width={80} />
            <RTooltip formatter={(v, n, it) => [`${fmtR(v)} (de ${fmtR(it.payload[`${n === rotuloA ? "A" : "B"}min`])} a ${fmtR(it.payload[`${n === rotuloA ? "A" : "B"}max`])})`, n]} />
            <Line type="stepAfter" dataKey="A" name={rotuloA} stroke="#20a490" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            <Line type="stepAfter" dataKey="B" name={rotuloB} stroke={T.azul} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      ) : <div style={{ fontSize: 12, color: T.inkSoft }}>Sem atendimentos com quantidade no período.</div>}
      <div style={{ display: "flex", gap: 14, fontSize: 11.5, color: T.inkSoft, margin: "2px 0 8px" }}>
        <span><b style={{ color: "#20a490" }}>━</b> {rotuloA}</span><span><b style={{ color: T.azul }}>━</b> {rotuloB}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <select value={lado} onChange={(e) => setLado(e.target.value)} style={{ ...campo, fontSize: 12 }} aria-label="Lado dos atendimentos">
          <option value="">Os dois lados ({fmtN((d.total_linhas.A || 0) + (d.total_linhas.B || 0))})</option>
          <option value="A">{rotuloA} ({fmtN(d.total_linhas.A || 0)})</option>
          <option value="B">{rotuloB} ({fmtN(d.total_linhas.B || 0)})</option>
        </select>
        <div style={{ flex: 1 }} />
        <button style={btn(false)} onClick={baixar} disabled={baixando}>{baixando ? "Exportando…" : "Exportar para planilha (CSV)"}</button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={TH}>Atendimento</th><th style={TH}>Apresentação</th><th style={TH}>Lado</th><th style={TH}>Prestador</th>
            <th style={{ ...TH, textAlign: "right" }}>Quantidade</th><th style={{ ...TH, textAlign: "right" }}>Valor por unidade</th>
            <th style={{ ...TH, textAlign: "right" }}>Glosa</th><th style={{ ...TH, textAlign: "right" }}>Valor pago</th><th style={TH}>Guia</th>
          </tr></thead>
          <tbody>
            {d.linhas.map((l, n) => (
              <tr key={n} style={{ opacity: l.glosada_inteira ? 0.55 : 1 }}>
                <td style={TD}>{dataBR(l.dt_atendimento)}</td><td style={TD}>{dataBR(l.dt_apresentacao)}</td>
                <td style={{ ...TD, fontSize: 11.5 }}>{l.lado === "A" ? "1º lado" : "2º lado"}</td>
                <td style={TD}>{l.nm_prestador}{l.nr_cpf_cgc && <div style={{ fontSize: 11, color: T.inkSoft }}>{fmtDoc(l.nr_cpf_cgc)}</div>}</td>
                <td style={NUM}>{fmtN(l.qt, 2)}</td><td style={{ ...NUM, fontWeight: 700 }}>{l.glosada_inteira ? "glosada" : fmtR(l.vl_unit)}</td>
                <td style={NUM}>{fmtR(l.vl_glosa)}</td><td style={NUM}>{fmtR(l.vl_pago)}</td><td style={{ ...TD, fontSize: 11.5 }}>{l.nr_guia || "—"}</td>
              </tr>))}
            {d.linhas.length === 0 && <tr><td colSpan={9} style={{ ...TD, color: T.inkSoft }}>Nenhum atendimento.</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 12, color: T.inkSoft }}>
        <button style={btn(false)} disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>Anterior</button>
        <span>Página {fmtN(pagina)} de {fmtN(paginas)} · {fmtN(total)} atendimento(s){lado ? ` de ${rot[lado]}` : ""}</span>
        <button style={btn(false)} disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)}>Próxima</button>
        <span style={{ marginLeft: "auto" }}>Sem nome do beneficiário: para preço não precisa.</span>
      </div>
    </div>
  );
}

function DetalheSintetico({ params, procedimento, rotuloA, rotuloB }) {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  useEffect(() => {
    let vivo = true;
    setD(null); setErro(null);
    api.get(`${BASE}/item`, { params: { ...params, procedimento, limite: 50 } })
      .then((r) => vivo && setD(r.data)).catch((e) => vivo && setErro(erroDe(e, "Não foi possível abrir o procedimento.")));
    return () => { vivo = false; };
  }, [params, procedimento]);
  if (erro) return <div style={{ color: T.risco, fontSize: 12, padding: 8 }}>{erro}</div>;
  if (!d) return <div style={{ color: T.inkSoft, fontSize: 12, padding: 8 }}>Carregando prestadores…</div>;
  const bloco = (lado, rotulo) => {
    const t = d.totais[lado];
    const itens = d.composicao[lado];
    if (!itens.length) return null;
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 4 }}>
          {rotulo}: {fmtN(t.qt)} un. a {fmtR(t.unit)} (de {fmtR(t.unit_min)} a {fmtR(t.unit_max)}), {fmtN(t.qt_prestadores)} prestador(es)
          {t.linhas_glosadas > 0 && <span style={{ color: T.inkSoft, fontWeight: 400 }}>; {fmtN(t.linhas_glosadas)} conta(s) glosada(s) por inteiro</span>}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={TH}>Prestador</th><th style={{ ...TH, textAlign: "right" }}>Quantidade</th>
            <th style={{ ...TH, textAlign: "right" }}>Valor pago</th><th style={{ ...TH, textAlign: "right" }}>Preço médio por unidade</th>
            <th style={{ ...TH, textAlign: "right" }}>Glosa</th><th style={{ ...TH, textAlign: "right" }}>Coparticipação</th>
            <th style={{ ...TH, textAlign: "right" }}>Parte do lado</th>
          </tr></thead>
          <tbody>
            {itens.map((p) => (
              <tr key={p.cd_prestador}>
                <td style={TD}>{p.nm_prestador}{lado === "FORA" && <span style={{ color: T.inkSoft }}> ({p.rotulo_rede})</span>}</td>
                <td style={NUM}>{fmtN(p.qt)}{p.qt_estimada ? " *" : ""}</td>
                <td style={NUM}>{fmtR(p.vl)}</td><td style={NUM}>{fmtR(p.unit)}</td>
                <td style={NUM}>{fmtR(p.vl_glosa)}</td><td style={NUM}>{fmtR(p.vl_copart)}</td>
                <td style={NUM}>{fmtP(p.pct_do_lado)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  return (
    <div>
      {bloco("A", rotuloA)}{bloco("B", rotuloB)}{bloco("FORA", "Fora dos dois lados")}
      <div style={{ fontSize: 11, color: T.inkSoft }}>* conta sem quantidade informada no sistema de origem: contada como 1.</div>
    </div>
  );
}

// ---------- prestadores e classificação ----------
function Prestadores({ mes, mesAte, gestor, categorias, onMudou }) {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [recarga, setRecarga] = useState(0);
  useEffect(() => {
    let vivo = true;
    setErro(null);
    api.get(`${BASE}/prestadores`, { params: { mes, mes_ate: mesAte || undefined, rede: filtro || undefined, busca: buscaAplicada || undefined, limite: 300 } })
      .then((r) => vivo && setD(r.data)).catch((e) => vivo && setErro(erroDe(e, "Não foi possível carregar os prestadores.")));
    return () => { vivo = false; };
  }, [mes, mesAte, filtro, buscaAplicada, recarga]);

  const marcar = async (cd, rede) => {
    try {
      await api.put(`${BASE}/prestadores/${encodeURIComponent(cd)}`, { rede: rede || null });
      toast.success(rede ? "Categoria marcada" : "Prestador devolvido à regra");
      setRecarga((n) => n + 1); onMudou();
    } catch (e) { toast.error(erroDe(e, "Não foi possível salvar.")); }
  };
  if (erro) return <div style={{ color: T.risco, fontSize: 13 }}>{erro}</div>;
  if (!d) return <div style={{ color: T.inkSoft, fontSize: 13 }}>Carregando prestadores…</div>;
  const r = d.resumo;
  return (
    <div>
      <div style={{ fontSize: 13, color: T.ink, marginBottom: 6 }}>
        <b>{fmtP(r.pct_valor_classificado)}</b> do valor do período está classificado; {fmtR(r.vl_sem_classificacao)} sem categoria ou em Outros.
      </div>
      <div style={{ height: 8, background: "#eef1f4", borderRadius: 4, overflow: "hidden", marginBottom: 10 }}
           role="progressbar" aria-valuenow={r.pct_valor_classificado || 0} aria-valuemin={0} aria-valuemax={100}>
        <div style={{ width: `${r.pct_valor_classificado || 0}%`, height: "100%", background: T.teal }} />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)} style={campo} aria-label="Filtrar por categoria">
          <option value="">Todas as categorias</option>
          {categorias.map((c) => <option key={c.codigo} value={c.codigo}>{c.rotulo}</option>)}
        </select>
        <input value={busca} onChange={(e) => setBusca(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setBuscaAplicada(busca.trim())}
               placeholder="Nome ou código do prestador" aria-label="Buscar prestador" style={{ ...campo, minWidth: 220 }} />
        <button style={btn(false)} onClick={() => setBuscaAplicada(busca.trim())}>Buscar</button>
      </div>
      <div style={{ maxHeight: 420, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={TH}>Prestador</th><th style={{ ...TH, textAlign: "right" }}>Valor pago</th>
            <th style={{ ...TH, textAlign: "right" }}>Acumulado</th><th style={TH}>Categoria</th>
            <th style={TH}>No cadastro do ERP</th><th style={TH}>Por quê</th>
          </tr></thead>
          <tbody>
            {d.prestadores.map((p) => (
              <tr key={p.cd_prestador}>
                <td style={TD}>{p.nm_prestador}<div style={{ fontSize: 11, color: T.inkSoft }}>código {p.cd_prestador}</div></td>
                <td style={NUM}>{fmtR(p.vl)}</td>
                <td style={NUM}>{fmtP(p.acumulado_pct)}</td>
                <td style={TD}>
                  {gestor ? (
                    <select value={p.rede} onChange={(e) => marcar(p.cd_prestador, e.target.value)} style={campo} aria-label={`Categoria de ${p.nm_prestador}`}>
                      {categorias.map((c) => <option key={c.codigo} value={c.codigo}>{c.rotulo}</option>)}
                    </select>
                  ) : p.rotulo_rede}
                </td>
                <td style={{ ...TD, fontSize: 12, color: T.inkSoft }}>
                  {[p.tipo_erp && `tipo ${p.tipo_erp}`, p.grupo_erp && `grupo ${p.grupo_erp}`,
                    p.contratualizacao_erp && `contratualização ${p.contratualizacao_erp}`,
                    p.credenciamento_erp && `credenciamento ${p.credenciamento_erp}`].filter(Boolean).join("; ") || "fora do cadastro"}
                </td>
                <td style={{ ...TD, fontSize: 12 }}>
                  {p.origem === "gestor" ? (
                    <span>marcado à mão{p.atualizado_por ? ` por ${p.atualizado_por}` : ""} em {dataHoraBR(p.atualizado_em)}
                      {gestor && <button onClick={() => marcar(p.cd_prestador, null)} style={{ ...btn(false), marginLeft: 6, padding: "2px 8px" }}>voltar à regra</button>}
                    </span>
                  ) : (p.regra || <span style={{ color: T.inkSoft }}>nenhuma regra se aplicou</span>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d.truncado && <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 4 }}>Mostrando os 300 maiores; use a busca para achar outros.</div>}
    </div>
  );
}

// ---------- regra de classificação ----------
function Regra({ gestor, onMudou }) {
  const [d, setD] = useState(null);
  const [linhas, setLinhas] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const carregar = useCallback(() => api.get(`${BASE}/regras`).then((r) => {
    setD(r.data);
    setLinhas(r.data.regras.map((x) => ({ ...x, tipos: (x.tipos || []).join(","), grupos: (x.grupos || []).join(","),
      contratualizacao: (x.contratualizacao || []).join(","), credenciamento: (x.credenciamento || []).join(",") })));
  }).catch((e) => setErro(erroDe(e, "Não foi possível carregar a regra."))), []);
  useEffect(() => { carregar(); }, [carregar]);

  const muda = (i, k, v) => setLinhas((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const salvar = async () => {
    setSalvando(true);
    try {
      await api.put(`${BASE}/regras`, { regras: linhas.map((l, i) => ({ ...l, ordem: (i + 1) * 10 })) });
      toast.success("Regra salva e prestadores reclassificados"); await carregar(); onMudou();
    } catch (e) { toast.error(erroDe(e, "Não foi possível salvar a regra.")); } finally { setSalvando(false); }
  };
  const reclassificar = async () => {
    setSalvando(true);
    try {
      const r = await api.post(`${BASE}/reclassificar`);
      if (r.data?.cadastro?.ok === false) toast.error("O cadastro do ERP não respondeu; reclassificado com a cópia que já existia.");
      else toast.success("Cadastro relido e prestadores reclassificados");
      await carregar(); onMudou();
    } catch (e) { toast.error(erroDe(e, "Não foi possível reclassificar.")); } finally { setSalvando(false); }
  };
  if (erro) return <div style={{ color: T.risco, fontSize: 13 }}>{erro}</div>;
  if (!d) return <div style={{ color: T.inkSoft, fontSize: 13 }}>Carregando a regra…</div>;
  const rot = Object.fromEntries(d.categorias.map((c) => [c.codigo, c.rotulo]));
  const dom = d.dominios;
  return (
    <div>
      <div style={{ fontSize: 12, color: T.inkSoft, marginBottom: 8 }}>
        Cadastro do ERP: {fmtN(d.cadastro.prestadores)} prestadores, lido em {dataHoraBR(d.cadastro.sincronizado_em)}.
        A primeira linha que se aplica decide. Campos vazios valem para qualquer valor; vários códigos separados por vírgula.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <th style={TH}>Vai para</th><th style={TH}>Tipo</th><th style={TH}>Grupo</th>
          <th style={TH}>Contratualização</th><th style={TH}>Credenciamento</th><th style={TH}>Descrição</th>
          <th style={{ ...TH, textAlign: "right" }}>Prestadores</th>{gestor && <th style={TH} />}
        </tr></thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={i}>
              <td style={TD}>{gestor ? (
                <select value={l.rede} onChange={(e) => muda(i, "rede", e.target.value)} style={campo} aria-label="Categoria">
                  {d.categorias.filter((c) => c.codigo !== "NAO_CLASSIFICADO").map((c) => <option key={c.codigo} value={c.codigo}>{c.rotulo}</option>)}
                </select>) : rot[l.rede]}</td>
              {["tipos", "grupos", "contratualizacao", "credenciamento"].map((k) => (
                <td key={k} style={TD}>{gestor
                  ? <input value={l[k]} onChange={(e) => muda(i, k, e.target.value)} style={{ ...campo, width: 90 }} aria-label={k} />
                  : (l[k] || "qualquer")}</td>))}
              <td style={TD}>{gestor
                ? <input value={l.descricao || ""} onChange={(e) => muda(i, "descricao", e.target.value)} style={{ ...campo, width: "100%" }} aria-label="Descrição" />
                : l.descricao}</td>
              <td style={NUM}>{vazio(l.qt_prestadores) ? "" : fmtN(l.qt_prestadores)}</td>
              {gestor && <td style={TD}><button onClick={() => setLinhas((ls) => ls.filter((_, j) => j !== i))} style={{ ...btn(false), padding: "3px 6px" }} aria-label="Remover linha"><Trash2 size={13} /></button></td>}
            </tr>
          ))}
        </tbody>
      </table>
      {gestor && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <button style={btn(false)} onClick={() => setLinhas((ls) => [...ls, { rede: "DIRETA", tipos: "", grupos: "", contratualizacao: "", credenciamento: "", descricao: "", ativa: true }])}><Plus size={13} /> Nova linha</button>
          <button style={btn(true)} disabled={salvando} onClick={salvar}><Save size={13} /> Salvar regra</button>
          <button style={btn(false)} disabled={salvando} onClick={reclassificar}><RefreshCw size={13} /> Reler cadastro do ERP</button>
        </div>
      )}
      <details style={{ marginTop: 10, fontSize: 12, color: T.inkSoft }}>
        <summary style={{ cursor: "pointer" }}>Códigos que existem no cadastro do ERP</summary>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10, marginTop: 6 }}>
          <div><b>Tipo</b>{dom.tipos.map((x) => <div key={x.codigo}>{x.codigo}: {x.descricao} ({fmtN(x.qt)})</div>)}</div>
          <div><b>Grupo</b>{dom.grupos.map((x) => <div key={x.codigo}>{x.codigo}: {x.descricao} ({fmtN(x.qt)})</div>)}</div>
          <div><b>Contratualização</b>{dom.contratualizacao.map((x) => <div key={x.codigo}>{x.codigo} ({fmtN(x.qt)})</div>)}
            <b style={{ display: "block", marginTop: 6 }}>Credenciamento</b>{dom.credenciamento.map((x) => <div key={x.codigo}>{x.codigo} ({fmtN(x.qt)})</div>)}</div>
        </div>
      </details>
    </div>
  );
}

// ---------- a seção ----------
export default function RedePrecos({ mes, tp, gestor, meses = [] }) {
  const [mesAte, setMesAte] = useState("");
  const [ladoA, setLadoA] = useState("rede:DIRETA");
  const [ladoB, setLadoB] = useState("rede:INTERCAMBIO");
  const [minQt, setMinQt] = useState(10);
  const [semCopart, setSemCopart] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [prestOpcoes, setPrestOpcoes] = useState([]);
  const [hospitais, setHospitais] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [comp, setComp] = useState(null);
  const [serie, setSerie] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [painel, setPainel] = useState("");
  const [verPoucos, setVerPoucos] = useState(false);
  const [verTecnico, setVerTecnico] = useState(false);
  const [verMetodo, setVerMetodo] = useState(false);
  const [versao, setVersao] = useState(0);
  // Período por data do atendimento (padrão: compara preço vigente) ou por competência (#242)
  const [base, setBase] = useState("atendimento");
  const [escolhidos, setEscolhidos] = useState({});

  const hospSel = (ladoA.startsWith("hospital:") && ladoA.split(":")[1]) || "";
  const extrasHosp = useMemo(() => {
    const o = {};
    (hospitais || []).forEach((h) => { o[`hospital:${h.raiz}:direto`] = `${h.nome} · cartão ELOSAÚDE`; o[`hospital:${h.raiz}:unimed`] = `${h.nome} · cartão Unimed`; });
    return { ...o, ...escolhidos };
  }, [hospitais, escolhidos]);
  // opções dos campos de busca
  const opcoesHosp = useMemo(() => (hospitais || []).map((h) => ({
    valor: h.raiz, rotulo: h.nome,
    detalhe: `CNPJ ${h.raiz.replace(/(\d{2})(\d{3})(\d{3})/, "$1.$2.$3")} · cartão ELOSAÚDE R$ ${fmtK(h.vl_direto)} · cartão Unimed R$ ${fmtK(h.vl_unimed)}`,
  })), [hospitais]);
  const opcoesLado = useMemo(() => {
    const o = categorias.filter((c) => c.comparavel).map((c) => ({ valor: `rede:${c.codigo}`, rotulo: c.rotulo, grupo: "Categorias de rede" }));
    const ind = opcaoIndireta(categorias);
    if (ind) o.push({ valor: ind.valor, rotulo: ind.rotulo, grupo: "Categorias de rede" });
    // do "mesmo hospital", só o escolhido no atalho (com todos, "unimed" casava com o cartão Unimed de cada hospital)
    Object.entries(extrasHosp).filter(([v]) => !v.startsWith("hospital:") || v.split(":")[1] === hospSel)
      .forEach(([v, r]) => o.push({ valor: v, rotulo: r, grupo: v.startsWith("hospital:") ? "Mesmo hospital pelos dois cartões" : "Escolhidos na busca" }));
    prestOpcoes.forEach((p) => o.push({ valor: `prestador:${p.cd_prestador}`, rotulo: p.nm_prestador, grupo: "Maiores valores no período",
      detalhe: [p.rotulo_rede, p.vl ? `R$ ${fmtK(p.vl)} no período` : null].filter(Boolean).join(" · ") }));
    return o;
  }, [categorias, extrasHosp, prestOpcoes, hospSel]);
  const rotuloLado = (v) => opcoesLado.find((o) => o.valor === v)?.rotulo || extrasHosp[v] || (v.startsWith("prestador:") ? `Prestador ${v.slice(10)}` : v);
  const buscarPrestador = useCallback((q) => api.get(`${BASE}/prestadores/busca`, { params: { q } }).then((r) => (r.data || []).map((p) => ({
    valor: `prestador:${p.cd_prestador}`, rotulo: p.nm_prestador || `Prestador ${p.cd_prestador}`,
    detalhe: [p.nr_cpf_cgc ? `${p.nr_cpf_cgc.length === 14 ? "CNPJ" : "CPF"} ${fmtDoc(p.nr_cpf_cgc)}` : "sem CPF/CNPJ no cadastro", `código ${p.cd_prestador}`, p.rotulo_rede].filter(Boolean).join(" · "),
  }))), []);
  // escolher num lado: se veio da busca no cadastro, guarda o nome para o campo mostrar
  const escolherLado = (setLado) => (v, o) => {
    if (v.startsWith("prestador:") && o && !prestOpcoes.some((p) => `prestador:${p.cd_prestador}` === v)) {
      setEscolhidos((e) => ({ ...e, [v]: o.rotulo }));
    }
    setLado(v);
  };
  const escolherHospital = (raiz) => {
    if (!raiz) { setLadoA("rede:DIRETA"); setLadoB("rede:INTERCAMBIO"); return; }
    setLadoA(`hospital:${raiz}:direto`); setLadoB(`hospital:${raiz}:unimed`);
  };

  // o "até" nunca fica antes do mês escolhido acima
  useEffect(() => { setMesAte((m) => (m && m <= mes ? "" : m)); }, [mes]);

  const params = useMemo(() => ({
    mes, mes_ate: mesAte || undefined, tp: tp || undefined, lado_a: ladoA, lado_b: ladoB,
    min_qt: minQt, sem_copart: semCopart || undefined, base,
  }), [mes, mesAte, tp, ladoA, ladoB, minQt, semCopart, base]);

  useEffect(() => {
    api.get(`${BASE}/regras`).then((r) => setCategorias(r.data.categorias)).catch(() => setCategorias([]));
  }, [versao]);
  useEffect(() => {
    if (!mes) return undefined;
    let vivo = true;
    api.get(`${BASE}/prestadores`, { params: { mes, mes_ate: mesAte || undefined, limite: 80, base } })
      .then((r) => vivo && setPrestOpcoes(r.data.prestadores.filter((p) => p.cd_prestador !== "?"))).catch(() => {});
    setHospitais(null);
    api.get(`${BASE}/hospitais`, { params: { mes, mes_ate: mesAte || undefined, base } })
      .then((r) => vivo && setHospitais(r.data.hospitais || [])).catch(() => vivo && setHospitais([]));
    api.get(`${BASE}/resumo`, { params: { mes, mes_ate: mesAte || undefined, tp: tp || undefined, base } })
      .then((r) => vivo && setResumo(r.data)).catch(() => vivo && setResumo(null));
    return () => { vivo = false; };
  }, [mes, mesAte, tp, versao, base]);
  useEffect(() => {
    if (!mes) return undefined;
    let vivo = true;
    setCarregando(true); setErro(null);
    api.get(`${BASE}/comparativo`, { params: { ...params, limite: 300 } })
      .then((r) => vivo && setComp(r.data))
      .catch((e) => vivo && setErro(erroDe(e, "Não foi possível comparar.")))
      .finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
  }, [params, mes, versao]);

  // Evolução sempre visível: o intervalo escolhido ou, sem ele, os 6 meses que
  // terminam na competência (sem passar do primeiro mês sincronizado).
  const evoInicio = useMemo(() => {
    if (!mes) return "";
    if (mesAte) return mes;
    const ini = mesMenos(mes, 5);
    return meses.length && ini < meses[0] ? meses[0] : ini;
  }, [mes, mesAte, meses]);
  const evoFim = mesAte || mes;
  useEffect(() => {
    if (!mes || !evoInicio || evoInicio === evoFim) { setSerie(null); return undefined; }
    let vivo = true;
    setSerie(null);
    api.get(`${BASE}/evolucao`, { params: { ...params, mes: evoInicio, mes_ate: evoFim } })
      .then((r) => vivo && setSerie(r.data.serie)).catch(() => vivo && setSerie([]));
    return () => { vivo = false; };
  }, [params, mes, evoInicio, evoFim, versao]);

  // Onde está a diferença: volume de A x (preço de B - preço de A), por procedimento.
  const pesos = useMemo(() => {
    if (!comp?.comparaveis?.length) return [];
    const itens = comp.comparaveis
      .filter((i) => !vazio(i.unit_a) && !vazio(i.unit_b))
      .map((i) => ({ nome: curto(i.descricao || i.procedimento), procedimento: i.procedimento, peso: i.qt_a * (i.unit_b - i.unit_a) }));
    const fav = [...itens].filter((x) => x.peso > 0).sort((x, y) => y.peso - x.peso).slice(0, 6);
    const contra = [...itens].filter((x) => x.peso < 0).sort((x, y) => x.peso - y.peso).slice(0, 4);
    return [...fav, ...contra];
  }, [comp]);

  if (!mes) return null;
  const mesmoLado = ladoA === ladoB;
  const ag = comp?.agregado, cob = comp?.cobertura, cl = comp?.classificacao;
  const ra = comp?.rotulo_a || "Lado A", rb = comp?.rotulo_b || "Lado B";
  const periodo = mesAte ? `${mesBR(mes)} a ${mesBR(mesAte)}` : mesBR(mes);
  // com hospital ou prestador num dos lados, o gráfico mostra só os dois lados (#242);
  // só com categorias, a visão da carteira por categoria.
  const porLado = [ladoA, ladoB].some((v) => v.startsWith("prestador:") || v.startsWith("hospital:"));

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <b style={{ fontSize: 15, color: T.ink }}>Rede e preços: quanto custa cada procedimento ({periodo})</b>
        <InfoTip w={420} texto={TIP.tela} />
        {carregando && comp && <span style={{ fontSize: 11, color: T.inkSoft }}>atualizando…</span>}
        <div style={{ flex: 1 }} />
        <button style={btn(verMetodo)} onClick={() => setVerMetodo((v) => !v)} aria-expanded={verMetodo}>
          {verMetodo ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Como esta tela calcula (passo a passo)
        </button>
      </div>
      {verMetodo && <Metodologia comp={comp} ra={comp?.rotulo_a || "o primeiro lado"} rb={comp?.rotulo_b || "o segundo lado"} minQt={minQt} semCopart={semCopart} />}

      {/* 1. O QUE COMPARAR: atalho do mesmo hospital e os dois lados, cada um com busca */}
      <div style={{ marginTop: 12, background: "#f7f9fa", border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 12px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: T.inkSoft, marginBottom: 6 }}>O que comparar</div>
        <CampoBusca rotulo="Mesmo hospital pelos dois cartões (atalho)" ajuda={<InfoTip w={340} texto={TIP.hospital} />} largura={460}
          valor={hospSel} rotuloValor={hospSel ? (hospitais || []).find((h) => h.raiz === hospSel)?.nome || `CNPJ ${hospSel}` : ""}
          opcoes={opcoesHosp} onEscolher={(v) => escolherHospital(v)} onLimpar={() => escolherHospital("")}
          placeholder={hospitais === null ? "Carregando hospitais…" : hospitais.length ? "Digite o nome ou o CNPJ do hospital" : "Nenhum hospital nos dois cartões no período"}
          vazio="Nenhum hospital com atendimento pelos dois cartões no período" ariaLabel="Mesmo hospital pelos dois cartões" />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 10 }}>
          <CampoBusca rotulo="1º lado" largura={340} valor={ladoA} rotuloValor={rotuloLado(ladoA)} opcoes={opcoesLado}
            buscarRemoto={buscarPrestador} onEscolher={escolherLado(setLadoA)} placeholder="Categoria, prestador, código ou CNPJ" ariaLabel="Primeiro lado" />
          <span style={{ fontSize: 13, fontWeight: 800, color: T.inkSoft, padding: "0 2px 7px" }}>×</span>
          <CampoBusca rotulo="2º lado (comparado com)" largura={340} valor={ladoB} rotuloValor={rotuloLado(ladoB)} opcoes={opcoesLado}
            buscarRemoto={buscarPrestador} onEscolher={escolherLado(setLadoB)} placeholder="Categoria, prestador, código ou CNPJ" ariaLabel="Comparado com" />
          {opcaoIndireta(categorias) && <span style={{ paddingBottom: 7 }}><InfoTip w={280} texto={TIP.indireta} /></span>}
        </div>
      </div>

      {/* 2. COMO MEDIR: base de data, intervalo, volume mínimo, coparticipação */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginTop: 10, fontSize: 12, color: T.inkSoft }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase" }}>Como medir</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          Período por <InfoTip w={360} texto={TIP.base} />
          <span style={{ display: "inline-flex", border: `1px solid ${T.line}`, borderRadius: 8, overflow: "hidden" }} role="group" aria-label="Período por">
            {[["atendimento", "Data do atendimento"], ["competencia", "Competência"]].map(([k, r]) => (
              <button key={k} onClick={() => setBase(k)} style={{ border: 0, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", background: base === k ? T.ink : "#fff", color: base === k ? "#fff" : T.ink }}>{r}</button>))}
          </span>
        </span>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          até o mês <input type="month" value={mesAte} min={mes} onChange={(e) => setMesAte(e.target.value && e.target.value > mes ? e.target.value : "")} style={campo} aria-label="Até o mês" />
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          volume mínimo por lado <input type="number" min={0} value={minQt} onChange={(e) => setMinQt(Math.max(0, Number(e.target.value) || 0))} style={{ ...campo, width: 64 }} aria-label="Volume mínimo por lado" />
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.ink }}>
          <input type="checkbox" checked={semCopart} onChange={(e) => setSemCopart(e.target.checked)} /> descontar coparticipação
        </label>
      </div>
      {base === "atendimento" && <div style={{ fontSize: 11.5, color: "#8a5a00", marginTop: 4 }}>Por data do atendimento, os meses recentes ficam incompletos até as contas chegarem (no intercâmbio, pode levar meses).</div>}
      {mesmoLado && <div style={{ marginTop: 8, fontSize: 12, color: T.risco }}>Os dois lados são o mesmo: escolha lados diferentes.</div>}

      {porLado && comp?.total_lados && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 4, display: "flex", gap: 6, alignItems: "center" }}>
            Valor pago a cada lado no período: {fmtR((comp.total_lados.A || 0) + (comp.total_lados.B || 0))} <InfoTip w={340} texto={TIP.totalLados} />
          </div>
          <ResponsiveContainer width="100%" height={96}>
            <BarChart layout="vertical" data={[{ rotulo: ra, vl: comp.total_lados.A || 0, k: "A" }, { rotulo: rb, vl: comp.total_lados.B || 0, k: "B" }].map((r) => ({ ...r, etiqueta: fmtR(r.vl) }))} margin={{ top: 4, right: 130, left: 8, bottom: 4 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="rotulo" width={300} tick={{ fontSize: 11 }} interval={0} />
              <RTooltip formatter={(v) => fmtR(v)} />
              <Bar dataKey="vl" name="Valor pago" radius={[0, 4, 4, 0]}>
                <Cell fill="#20a490" /><Cell fill={T.azul} />
                <LabelList dataKey="etiqueta" position="right" style={{ fontSize: 11, fill: T.ink }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {!porLado && resumo && resumo.redes.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 4, display: "flex", gap: 6, alignItems: "center" }}>
            Valor pago por categoria de rede: {fmtR(resumo.total)} <InfoTip w={340} texto={TIP.resumo} />
          </div>
          <ResponsiveContainer width="100%" height={Math.max(120, resumo.redes.length * 28 + 20)}>
            <BarChart layout="vertical" data={resumo.redes.map((r) => ({ ...r, etiqueta: `${fmtK(r.vl)} (${fmtP(r.pct)})` }))} margin={{ top: 4, right: 110, left: 8, bottom: 4 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="rotulo" width={210} tick={{ fontSize: 11 }} interval={0} />
              <RTooltip formatter={(v) => fmtR(v)} />
              <Bar dataKey="vl" name="Valor pago" radius={[0, 4, 4, 0]}>
                {resumo.redes.map((r) => (
                  <Cell key={r.rede} fill={r.rede === "DIRETA" ? "#20a490" : r.rede === "INTERCAMBIO" ? T.azul : r.rede === "NAO_CLASSIFICADO" || r.rede === "OUTROS" ? T.ambar : "#b8c4cc"} />
                ))}
                <LabelList dataKey="etiqueta" position="right" style={{ fontSize: 11, fill: T.ink }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {cl?.aviso && (
        <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: T.ambarSoft, color: "#8a5a00", fontSize: 12, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{fmtP(cl.pct_sem_classificacao)} do valor do período ({fmtR(cl.vl_sem_classificacao)}) está sem categoria ou em Outros e ficou fora da comparação. A conclusão pode mudar quando esses prestadores forem classificados. <InfoTip w={320} texto={TIP.semClass} /></span>
        </div>
      )}

      {erro && <div style={{ marginTop: 12, color: T.risco, fontSize: 13 }}>{erro}</div>}
      {!comp && !erro && <div style={{ marginTop: 12, color: T.inkSoft, fontSize: 13 }}>Comparando…</div>}

      {comp && !mesmoLado && (
        <>
          <div style={{ marginTop: 14, padding: 14, borderRadius: 10, border: `1px solid ${T.line}`, background: "#fafbfc" }}>
            {cob.qt_proc_comparaveis === 0 ? (
              <div style={{ fontSize: 13, color: T.inkSoft }}>Nenhum procedimento feito pelos dois lados com o volume mínimo no período.</div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: T.ink, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  O volume que <b>{ra}</b> fez nos {fmtN(cob.qt_proc_comparaveis)} procedimentos comparáveis custou <b>{fmtR(ag.a_preco_a)}</b>.
                  Aos preços de <b>{rb}</b>, custaria <b>{fmtR(ag.a_preco_b)}</b>.
                  <InfoTip w={360} texto={TIP.diferenca} />
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginTop: 6, color: ag.economia_de_a >= 0 ? T.verde : T.risco }}>
                  {ag.economia_de_a >= 0 ? `${ra} saiu ${fmtR(ag.economia_de_a)} mais barato` : `${ra} saiu ${fmtR(-ag.economia_de_a)} mais caro`}
                </div>
                <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 6, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  Cobertura: {fmtP(cob.pct_a_comparavel)} do valor de {ra} e {fmtP(cob.pct_b_comparavel)} do valor de {rb} entraram na comparação.
                  {cob.qt_proc_abaixo_do_minimo > 0 && ` ${fmtN(cob.qt_proc_abaixo_do_minimo)} procedimento(s) abaixo do volume mínimo.`}
                  {(cob.linhas_glosadas_a + cob.linhas_glosadas_b) > 0 && ` ${fmtN(cob.linhas_glosadas_a + cob.linhas_glosadas_b)} conta(s) glosada(s) por inteiro ficaram fora do preço.`}
                  {cob.itens_com_qt_estimada > 0 && ` ${fmtN(cob.itens_com_qt_estimada)} item(ns) com quantidade estimada (*).`}
                  <InfoTip w={320} texto={TIP.cobertura} />
                </div>
              </>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 16, marginTop: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 4, display: "flex", gap: 6, alignItems: "center" }}>
                Diferença mês a mês (positivo: {ra} mais barato) <InfoTip w={300} texto={TIP.evolucao} />
              </div>
              {serie === null ? (
                <div style={{ fontSize: 12, color: T.inkSoft, padding: "30px 0" }}>Calculando mês a mês…</div>
              ) : serie.length < 2 ? (
                <div style={{ fontSize: 12, color: T.inkSoft, padding: "30px 0" }}>Sem meses suficientes para a evolução.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={serie.map((s) => ({ ...s, rotulo: mesBR(s.mes) }))} margin={{ top: 6, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
                    <XAxis dataKey="rotulo" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} width={70} />
                    <ReferenceLine y={0} stroke={T.inkSoft} />
                    <RTooltip formatter={(v) => fmtR(v)} labelFormatter={(l) => `Competência ${l}`} />
                    <Line type="monotone" dataKey="economia_de_a" name="Diferença" stroke="#20a490" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            {pesos.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 4, display: "flex", gap: 6, alignItems: "center" }}>
                  Onde está a diferença <InfoTip w={320} texto={TIP.ondeDiferenca} />
                </div>
                <ResponsiveContainer width="100%" height={Math.max(160, pesos.length * 24 + 20)}>
                  <BarChart layout="vertical" data={pesos} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtK} />
                    <YAxis type="category" dataKey="nome" width={230} tick={{ fontSize: 10 }} interval={0} />
                    <ReferenceLine x={0} stroke={T.inkSoft} />
                    <RTooltip formatter={(v) => [fmtR(v), v >= 0 ? `${ra} mais barato` : `${ra} mais caro`]} />
                    <Bar dataKey="peso" radius={3} cursor="pointer" onClick={(d) => setAberto(d?.procedimento ?? d?.payload?.procedimento ?? null)}>
                      {pesos.map((p) => <Cell key={p.procedimento} fill={p.peso >= 0 ? T.verde : T.risco} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {!mesmoLado && (
            <SimuladorReajuste params={params} ra={ra} rb={rb} gestor={gestor} mes={mes}
              aoAbrirCenario={(p) => {
                if (p.lado_a) setLadoA(p.lado_a);
                if (p.lado_b) setLadoB(p.lado_b);
                setMesAte(p.mes_ate && p.mes_ate > mes ? p.mes_ate : "");
                setBase(p.base || "competencia");
                if (p.min_qt !== undefined && p.min_qt !== null) setMinQt(Number(p.min_qt));
                setSemCopart(!!p.sem_copart);
              }} />
          )}

          {comp.comparaveis.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 4, display: "flex", gap: 6, alignItems: "center" }}>
                Preço por procedimento <InfoTip w={320} texto={TIP.preco} />
              </div>
              <div style={{ maxHeight: 460, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={TH}>Procedimento</th>
                    <th style={{ ...TH, textAlign: "right" }}>Quantidade {ra}</th><th style={{ ...TH, textAlign: "right" }}>Preço médio {ra}</th>
                    <th style={{ ...TH, textAlign: "right" }}>Quantidade {rb}</th><th style={{ ...TH, textAlign: "right" }}>Preço médio {rb}</th>
                    <th style={{ ...TH, textAlign: "right" }}>Diferença</th>
                  </tr></thead>
                  <tbody>
                    {comp.comparaveis.map((i) => (
                      <React.Fragment key={i.procedimento}>
                        <tr onClick={() => setAberto(aberto === i.procedimento ? null : i.procedimento)}
                            onKeyDown={(e) => e.key === "Enter" && setAberto(aberto === i.procedimento ? null : i.procedimento)}
                            tabIndex={0} aria-expanded={aberto === i.procedimento} style={{ cursor: "pointer" }}>
                          <td style={TD}>{aberto === i.procedimento ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {i.descricao || i.procedimento}
                            <span style={{ color: T.inkSoft, fontSize: 11 }}> {i.procedimento}</span></td>
                          <td style={NUM}>{fmtN(i.qt_a)}{i.qt_estimada ? " *" : ""}</td><td style={NUM}>{fmtR(i.unit_a)}</td>
                          <td style={NUM}>{fmtN(i.qt_b)}</td><td style={NUM}>{fmtR(i.unit_b)}</td>
                          <td style={{ ...NUM, color: vazio(i.dif_pct) ? T.inkSoft : i.dif_pct > 0 ? T.risco : T.verde }}>
                            {vazio(i.dif_pct) ? "—" : `${i.dif_pct > 0 ? "+" : ""}${fmtP(i.dif_pct)}`}
                          </td>
                        </tr>
                        {aberto === i.procedimento && (
                          <tr><td colSpan={6} style={{ padding: 8 }}><DetalheItem params={params} procedimento={i.procedimento} rotuloA={ra} rotuloB={rb} /></td></tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 4 }}>Preço médio = valor pago ÷ quantidade paga, somando os prestadores do lado. Diferença positiva: {ra} cobra mais caro por unidade. * quantidade estimada (conta sem quantidade contada como 1).</div>
            </div>
          )}

          {comp.abaixo_do_minimo.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <button style={btn(verPoucos)} onClick={() => setVerPoucos((v) => !v)}>
                {verPoucos ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {fmtN(cob.qt_proc_abaixo_do_minimo)} procedimento(s) abaixo do volume mínimo
              </button> <InfoTip w={300} texto={TIP.minimo} />
              {verPoucos && (
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
                  <thead><tr><th style={TH}>Procedimento</th><th style={{ ...TH, textAlign: "right" }}>Quantidade {ra}</th><th style={{ ...TH, textAlign: "right" }}>Preço médio {ra}</th><th style={{ ...TH, textAlign: "right" }}>Quantidade {rb}</th><th style={{ ...TH, textAlign: "right" }}>Preço médio {rb}</th></tr></thead>
                  <tbody>{comp.abaixo_do_minimo.slice(0, 50).map((i) => (
                    <tr key={i.procedimento}><td style={TD}>{i.descricao || i.procedimento}</td><td style={NUM}>{fmtN(i.qt_a)}</td><td style={NUM}>{fmtR(i.unit_a)}</td><td style={NUM}>{fmtN(i.qt_b)}</td><td style={NUM}>{fmtR(i.unit_b)}</td></tr>))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div style={{ marginTop: 14, fontSize: 12, color: T.inkSoft, borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
            <b style={{ color: T.ink }}>De onde vêm estes números</b>
            <ul style={{ margin: "6px 0 0 18px", padding: 0, lineHeight: 1.6 }}>
              <li>{comp.procedencia.texto.periodo}; {comp.procedencia.texto.filtros}.</li>
              <li>{comp.procedencia.texto.valor}.</li>
              <li>{comp.procedencia.texto.quantidade}.</li>
              <li>{comp.procedencia.texto.preco}.</li>
              {comp.procedencia.texto.regras.map((r) => <li key={r}>{r}</li>)}
            </ul>
            <button style={{ ...btn(verTecnico), marginTop: 6 }} onClick={() => setVerTecnico((v) => !v)}>
              {verTecnico ? "Esconder" : "Ver"} detalhe técnico (para auditoria)
            </button>
            {verTecnico && (
              <div style={{ fontFamily: MONO, fontSize: 11, marginTop: 6 }}>
                {Object.entries(comp.procedencia.tecnico).map(([k, v]) => (
                  <div key={k}>{k}: {typeof v === "string" ? v : `${v.a} | ${v.b}`}</div>))}
              </div>
            )}
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
        <button style={btn(painel === "prestadores")} onClick={() => setPainel(painel === "prestadores" ? "" : "prestadores")}>Prestadores e classificação</button>
        <InfoTip w={320} texto={TIP.classif} />
        <button style={btn(painel === "regra")} onClick={() => setPainel(painel === "regra" ? "" : "regra")}>Regra de classificação</button>
        <InfoTip w={320} texto={TIP.regra} />
      </div>
      {painel === "prestadores" && (
        <div style={{ marginTop: 10 }}>
          <Prestadores mes={mes} mesAte={mesAte} gestor={gestor} categorias={categorias} onMudou={() => setVersao((n) => n + 1)} />
        </div>
      )}
      {painel === "regra" && (
        <div style={{ marginTop: 10 }}>
          <Regra gestor={gestor} onMudou={() => setVersao((n) => n + 1)} />
        </div>
      )}
    </div>
  );
}
